import {
  Box,
  Button,
  Grid,
  TextField,
  Paper,
  MenuItem,
  Divider,
  alpha,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { Search } from '@mui/icons-material'
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import WSTextField from '../../Components/TextBox/WSTextBox';
import SectionHeader from "../components/header";
import { WSIcons } from "../../commonFunctions/iIcons";
import ConfirmationPopup from "../../Components/Confirmation/Confirmation";
import DataTable, { Column } from "../components/dataTable";
import { debounce } from "lodash";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useAuth } from "../../StateManagement/authContext";
import BlockIcon from '@mui/icons-material/Block';

type Block = {
  _id?: string;
  name: string;
  code: string;
  districtId: string;
};


const BlockManager = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [blocks, setBlock] = useState<Block[]>([]);
  const [editId, setEditId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5)
  const [isPopupOpen, setPopupOpen] = useState(false); //for delete;
  const [selectedIdForDlt, setSelectedIdForDlt] =
    useState<any>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOption, setSortOption] = useState("created_desc");

  const { handleSubmit, reset, setValue, control } = useForm<Block>();
  const { showMessage } = useSnackbar();
  const { user } = useAuth();


  const handleEdit = (block: Block) => {
    setValue("name", block.name);
    setValue("code", block.code);
    setEditId(block._id || null)
  };

  const fetchData = useCallback(
    debounce(async (pageVal: number, searchVal: string, limitVal: number, statusVal: string, sortVal: string) => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          page: String(pageVal),
          search: searchVal,
          limit: String(limitVal),
          ...(statusVal && { status: statusVal }),
          ...(sortVal && { sort: sortVal }),
        });

        const res = await callAPI(
          `blocks/getPaginated?${query.toString()}`,
          Method.GET
        );

        console.log('block res', res)

        if (res.success) {
          setBlock(res.blocks || []);
          setTotalPages(res.totalPages || 0);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    }, 400),
    [setLoading, setBlock, setTotalPages]
  );

  const onSubmit = async (data: Block) => {
    try {
      setSubmitting(true);

      let request = {
        name: data.name,
        code: data.code,
        district: user?.district,
        createdBy: user?.userId,
      }

      if (editId) {
        await callAPI(`blocks/update/${editId}`, Method.PUT, request);
        showMessage("Block Updated Successfully", "success");
      } else if (!editId && user?.userType === 'admin') {
        await callAPI("blocks/create", Method.POST, request);
        showMessage("New Block Created Successfully", "success");
      }

      reset();
      setEditId(null);
      fetchData(page, search, rowsPerPage, statusFilter, sortOption);
    } catch (error: any) {
      if (error.message === 'GlobalHandledError') return;
      console.error("Error while submitting block:", error);
      showMessage(error?.response?.data?.message || "An unexpected error occurred", "error");
    } finally {
      setSubmitting(false);
    }
  };


  const handleDelete = () => {
    showMessage("District Deleted Successfully", "success");
    fetchData(page, search, rowsPerPage, statusFilter, sortOption);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  useEffect(() => {
    fetchData(page, search, rowsPerPage, statusFilter, sortOption);
  }, [page, search, rowsPerPage, statusFilter, sortOption]);


  const confirmDelete = (data: any) => {
    setSelectedIdForDlt(data._id);
    setPopupOpen(true)
  };

  const handleCancel = () => {
    setEditId(null);
    reset();
  }

  const columns: Column[] = [
    { field: "code", label: "Code" },
    { field: "name", label: "Name" }
    // {
    //   field: "createdAt",
    //   label: "Created",
    //   sortable: true,
    //   type: "date",
    // },
  ];


  const actions = [
    { label: "Edit", handler: handleEdit },
    { label: "Delete", handler: confirmDelete },
  ]


  const getButtonText = () => {
    if (submitting && editId) return "Updating...";
    if (submitting) return "Creating...";
    if (editId) return "Update Block";
    return "Create New Block";
  };

  return (
    <Box p={3} mb={6}>
      {user?.userType === 'admin' ? <SectionHeader title={"Block Management"} icon={<BlockIcon fontSize={"large"} />} subtitle={editId ? "✏️ Update Block" : "➕ Add New Block"} /> : <SectionHeader title={"Block Management"} icon={<BlockIcon fontSize={"large"} />} subtitle={"✏️ Update Block"} />}

      {/* Form */}
      <Paper elevation={0} sx={{ p: 2, mb: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <WSTextField
                id="block"
                label="Block Name *"
                name="name"
                control={control}
                rules={{ required: "Block Name is required" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <WSTextField
                id="code"
                label="Block Code *"
                name="code"
                control={control}
                rules={{ required: "Block code is required" }}
              />
            </Grid>
            {user?.userType === 'admin' ? <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="medium"
                sx={{
                  background: editId ? "#1976d2" : "#4DD26B",
                  color: "white",
                  textTransform: "capitalize",
                  px: 4,
                  fontWeight: "bold",
                  "&:hover": {
                    background: editId ? "#1565c0" : "#4DD26B",
                  },
                }}
                disabled={submitting || !editId}
              >
                {getButtonText()}
              </Button>
              {editId && (
                <Button size="medium" onClick={handleCancel} sx={{ ml: 2 }} color="secondary">
                  Cancel
                </Button>
              )}
            </Grid> : editId && <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="medium"
                sx={{
                  background: editId ? "#1976d2" : "#4DD26B",
                  color: "white",
                  textTransform: "capitalize",
                  px: 4,
                  fontWeight: "bold",
                  "&:hover": {
                    background: editId ? "#1565c0" : "#4DD26B",
                  },
                }}
                disabled={submitting || !editId}
              >
                {getButtonText()}
              </Button>
              {editId && (
                <Button size="medium" onClick={handleCancel} sx={{ ml: 2 }} color="secondary">
                  Cancel
                </Button>
              )}
            </Grid>}
          </Grid>

        </form>
      </Paper>

      <Box
        display="flex" gap={2} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}
      >
        {/* 🔍 Search */}
        <TextField
          label="Search Blocks..."
          variant="outlined"
          value={search}
          onChange={handleSearchChange}
          fullWidth
          sx={{
            flex: 1,
            backgroundColor: (theme) => alpha(theme.palette.grey[50], 0.9),
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '& fieldset': {
                borderColor: '#ddd',
              },
              '&:hover fieldset': {
                borderColor: '#bbb',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#4A00E0',
                borderWidth: 2,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* ⬇️ Sort By */}
        <FormControl
          size="small"
          sx={{
            minWidth: 200,
            backgroundColor: '#f9f9fb',
            borderRadius: 1,
            boxShadow: 0,
          }}
        >
          <InputLabel id="sort-option-label">
            <WSIcons.Sort sx={{ mr: 0.5, fontSize: 18 }} /> Sort By
          </InputLabel>
          <Select
            labelId="sort-option-label"
            label="Sort By"
            value={sortOption}
            onChange={(e) => {
              setSortOption(e.target.value);
              setPage(1);
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: 1,
                  boxShadow: 1,
                },
              },
            }}
          >
            <MenuItem value="name_asc">🔤 Name (A → Z)</MenuItem>
            <MenuItem value="name_desc">🔡 Name (Z → A)</MenuItem>
            <MenuItem value="created_desc">🆕 Newest</MenuItem>
            <MenuItem value="created_asc">📜 Oldest</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <DataTable columns={columns} data={blocks}
        loading={loading} actions={actions} onPageChange={setPage}
        rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
        page={page} totalPages={totalPages} />



      {isPopupOpen && (
        <ConfirmationPopup
          apiUrl={"blocks/delete"}
          id={selectedIdForDlt}
          confirmMessage="Are you sure you want to delete this block?"
          open={isPopupOpen}
          onClose={() => setPopupOpen(false)}
          onConfirm={handleDelete}
          permanent
        />
      )}
    </Box>
  );
};

export default BlockManager;
